import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTeams } from "@/contexts/teams-provider";
import { TeamUserRolesEnum } from "@/enums/team-user-role";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { DialogDescription } from "@radix-ui/react-dialog";
import { Separator } from "@radix-ui/react-menubar";
import { TeamUserDTO, UserInstructionDTO } from "@shaarp/common";
import { Loader2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import { ButtonLoading } from "../button-loading";
import PageSkeleton from "../page-skeleton";
import { Button } from "../ui/button";
import { DialogFooter, DialogHeader } from "../ui/dialog";
import { TextareaAutosize } from "../ui/textarea-autosize";
import { useToast, useToastError } from "../ui/use-toast";

export default function DataTableCellUserInstruction({
  user,
}: {
  user: TeamUserDTO;
}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { isAdmin, isManager, isAdvisor } = useTeams();

  // Billing users don't have AI instructions
  // Admin, Manager, and Advisor can edit instructions
  const canEdit =
    user.role !== TeamUserRolesEnum.BILLING &&
    (isAdmin || isManager || isAdvisor);

  if (!canEdit) {
    return null;
  }

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        {t("components.dataTableUsers.instruction.edit")}
      </Button>

      {open && (
        <DialogUserInstructionEdit user={user} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

const DialogUserInstructionEdit = ({
  user,
  onClose,
}: {
  user: TeamUserDTO;
  onClose: () => void;
}) => {
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const { t } = useTranslation();

  const [userInstruction, setUserInstruction] =
    useState<UserInstructionDTO | null>(null);
  const [newInstruction, setNewInstruction] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const { data } = await api.get<UserInstructionDTO>(
          `/my/team/user/${user.id}/instruction`
        );
        setUserInstruction(data);
        setNewInstruction(data.content || "");
      } catch (error) {
        toastError();
        setUserInstruction(null);
        setNewInstruction("");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const save = async () => {
    try {
      setIsSaveLoading(true);
      await api.post(`/my/team/user/${user.id}/instruction`, {
        content: newInstruction,
      });

      toast({
        variant: "default",
        title: t("components.dataTableUsers.instruction.saveSuccess"),
        description: t(
          "components.dataTableUsers.instruction.saveSuccessDescription"
        ),
      });
    } catch (error) {
      toastError();
    } finally {
      setIsSaveLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[800px] w-full">
        <DialogHeader>
          <DialogTitle>
            {t("components.dataTableUsers.instruction.dialogTitle", {
              fullName: user.fullName,
            })}
          </DialogTitle>
          <DialogDescription>
            {userInstruction?.updatedAt && userInstruction?.lastUpdatedBy && (
              <div className="text-sm text-gray-400">
                {t("components.dataTableUsers.instruction.lastModified", {
                  date: new Date(userInstruction.updatedAt).toLocaleString(),
                  updatedBy: userInstruction.lastUpdatedBy.fullName,
                })}
              </div>
            )}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Separator className="my-4" />
          {isLoading && <PageSkeleton />}
          {!isLoading && (
            <TextareaAutosize
              placeholder={t(
                "components.dataTableUsers.instruction.placeholder"
              )}
              rows={20}
              value={newInstruction || ""}
              onChange={(e) => setNewInstruction(e.target.value)}
              className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          )}
          <Separator className="my-4" />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">
              {t("components.dataTableUsers.instruction.close")}
            </Button>
          </DialogClose>
          {isLoading || isSaveLoading ? (
            <Button disabled>
              <Loader2Icon className="w-4 h-4 animate-spin" />
            </Button>
          ) : (
            <ButtonLoading onClick={save} isLoading={isSaveLoading}>
              {t("components.dataTableUsers.instruction.save")}
            </ButtonLoading>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
